import { COMPANY_FIELD_PREFIX, LEAD_LIST_FIELDS_MAX, type TableScope } from "@gioia365/contracts";
import { normalizeSearchText } from "@gioia365/domain/search-text";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { ApiRequestError } from "../../api/http";
import { Button } from "../../design/components/Button";
import { Dialog } from "../../design/components/Dialog";
import { Drawer } from "../../design/components/Drawer";
import { Icon } from "../../design/components/Icon";
import { Reason } from "../../design/components/Reason";
import { useToast } from "../../design/components/Toast";
import "../../design/components/checkbox.css";
import "../../design/components/text-field.css";
import { messageForError } from "../../i18n/errors";
import { useT } from "../../i18n/locale";
import type { TabContext } from "../../session/tab-context";
import { useClearMyLayout, usePublishLayout, useSaveMyLayout, type TableLayoutState } from "./layout-queries";
import { sameSort, sortDescription, type LeadSort } from "./table-layout";
import { COLUMN_GROUP_ORDER, moveVisibleColumn, setColumnVisible, type ColumnDef, type LayoutSource } from "./table-layout";
import "./columns.css";

// «Colonne e vista» (piano M3 Task 6, D5 e D14; mock-up `setupTableView` e `VIEW_LEVELS`; UX_SYSTEM, «Configuratore
// Colonne»): un `Drawer` a destra con la vista in uso («Stai usando …») e le altre a cui tornare, «Cerca una colonna»,
// le colonne della tabella nell'ordine della vista («Cognome» ferma e prima, dal 6 ottobre 2026; «Mostra», «Sposta su»,
// «Sposta giù») e le altre per gruppi, spente. Ogni cambio si vede subito nella tabella e resta sul dispositivo finché non si salva:
// «Salva per me», e per chi ha «Vista dell'azienda» «Salva per tutta {organization}», con la conferma che dice cosa
// succede alle viste personali. I due pulsanti stanno nel piede del pannello accanto a «Chiudi», sempre in vista anche
// con la lista lunga, e soltanto quando c'è qualcosa da salvare (decisione P25): «Salva per me» per la disposizione non
// ancora salvata, «Salva per tutta {organization}» anche per la vista personale in uso. Il configuratore nomina il
// contesto («Vale per: elenco Lead»). «Chiudi» non salva.
//
// Nell'elenco Lead la vista porta anche l'ordinamento (LEAD-17, `sort`): uno scelto dall'intestazione e diverso da quello della
// vista in uso è qualcosa da salvare («Ordinamento da salvare: …»), con «Salva per me» o «Salva per tutta {organization}»; dopo un
// salvataggio, e tornando a una vista salvata, l'ordinamento viene di nuovo dalla vista (`onAdopted`). In una Smart List l'ordine è
// quello della lista, che si salva con «Salva» (SL-8): lì il pannello non riceve `sort`.

type Props = {
  tab: TabContext;
  scope: TableScope;
  organization: string;
  /** Il nome del contesto: «elenco Lead», oppure «la Smart List «…»». */
  context: string;
  layout: TableLayoutState;
  /** L'ordinamento della vista, soltanto per l'elenco Lead (LEAD-17); in una Smart List manca. */
  sort?: ViewSort;
  onClose: () => void;
};

/**
 * L'ordinamento di adesso, quello della vista salvata in uso (o quello di partenza, se la vista non ne ha uno) e che cosa fare
 * quando la pagina deve tornare a seguire la vista: togliere l'ordinamento dall'indirizzo.
 */
export type ViewSort = { current: LeadSort; saved: LeadSort; onAdopted: () => void };

const SOURCE_KEYS = {
  device: "tableView.sourceDevice",
  personal: "tableView.sourcePersonal",
  company: "tableView.sourceCompany",
  product: "tableView.sourceProduct",
} as const satisfies Record<LayoutSource, string>;

export function ColumnsDrawer({ tab, scope, organization, context, layout, sort, onClose }: Props) {
  const t = useT();
  const toast = useToast();
  const baseId = useId();
  const findId = `${baseId}-find`;
  const showId = `${baseId}-show`;
  const upId = `${baseId}-up`;
  const downId = `${baseId}-down`;
  const shownId = `${baseId}-shown`;
  const othersId = `${baseId}-others`;
  const layoutErrorId = `${baseId}-layout-error`;
  const catalogErrorId = `${baseId}-catalog-error`;
  const closeId = `${baseId}-close`;
  const findRef = useRef<HTMLInputElement>(null);
  const [find, setFind] = useState("");
  const [confirming, setConfirming] = useState(false);
  const saveMine = useSaveMyLayout(tab, scope);
  const clearMine = useClearMyLayout(tab, scope);
  const publish = usePublishLayout(tab, scope);
  const { catalog, resolved, server, change, pin, clearDevice, layoutStatus, catalogStatus, retryLayout, retryCatalog } = layout;
  // Finché le viste del server e i campi dell'azienda non sono arrivati, non si sa che cosa si salverebbe: niente
  // salvataggi (stati di UX_SYSTEM: caricamento ed errore).
  const layoutReady = layoutStatus === "ready";
  const canSave = layoutReady && catalogStatus === "ready";
  const columns = resolved.columns;
  // Il gestore della piattaforma non ha un'appartenenza, quindi nemmeno una vista personale (`403` dal server).
  const hasPersonalViews = tab.kind === "membership";

  // Si comincia da «Cerca una colonna», come «Cerca un filtro».
  useEffect(() => {
    findRef.current?.focus();
  }, []);

  // Il controllo appena usato riprende il fuoco anche quando la sua riga cambia posto o lista (mostra, sposta): dopo il
  // disegno che segue il cambio.
  const focusTarget = useRef<string | null>(null);
  useEffect(() => {
    if (focusTarget.current === null) return;
    document.getElementById(focusTarget.current)?.focus();
    focusTarget.current = null;
  });
  const setFocusTarget = (id: string) => {
    focusTarget.current = id;
  };

  const byId = new Map<string, ColumnDef>(catalog.map((column) => [column.id, column]));
  const key = normalizeSearchText(find);
  const matches = (column: ColumnDef) =>
    key === "" || normalizeSearchText(column.label).includes(key) || (column.fullLabel !== undefined && normalizeSearchText(column.fullLabel).includes(key));
  const entries = columns.flatMap((column) => {
    const def = byId.get(column.fieldId);
    return def ? [{ column, def }] : [];
  });
  const fixed = entries.find((entry) => entry.def.always);
  const shown = entries.filter((entry) => entry.column.visible && !entry.def.always);
  const hidden = entries.filter((entry) => !entry.column.visible);
  const shownMatches = shown.filter((entry) => matches(entry.def));
  const groups = COLUMN_GROUP_ORDER.map((group) => ({ group, entries: hidden.filter((entry) => entry.def.group === group && matches(entry.def)) })).filter(
    (entry) => entry.entries.length > 0,
  );
  const fixedMatches = fixed !== undefined && matches(fixed.def);
  const nothing = !fixedMatches && shownMatches.length === 0 && groups.length === 0;

  const rowId = (fieldId: string, part: string) => `${baseId}-${part}-${fieldId}`;
  // L'elenco legge al più `LEAD_LIST_FIELDS_MAX` campi dell'azienda (`fields=`): oltre, una colonna resterebbe vuota anche con il
  // valore. Arrivati al tetto, gli altri campi dell'azienda non si mostrano finché non se ne toglie uno (rilievo di Codex sulla #62).
  const isCompanyField = (fieldId: string) => fieldId.startsWith(COMPANY_FIELD_PREFIX);
  const companyFull = shown.filter((entry) => isCompanyField(entry.column.fieldId)).length >= LEAD_LIST_FIELDS_MAX;
  const companyFullId = `${baseId}-company-full`;
  const blockedReason = (fieldId: string) => (companyFull && isCompanyField(fieldId) ? companyFullId : undefined);

  function toggle(fieldId: string, visible: boolean) {
    if (visible && blockedReason(fieldId) !== undefined) return;
    change(setColumnVisible(columns, fieldId, visible));
    setFocusTarget(rowId(fieldId, "show"));
  }

  function move(fieldId: string, step: -1 | 1) {
    const index = shown.findIndex((entry) => entry.column.fieldId === fieldId);
    // Le visibili contano «Cognome» al posto 0: la prima delle altre è al posto 1.
    const target = index + 1 + step;
    change(moveVisibleColumn(columns, fieldId, target));
    // Arrivata in cima o in fondo, il pulsante si spegne: il fuoco passa all'altro della stessa riga.
    const atEdge = step === -1 ? target <= 1 : target >= shown.length;
    setFocusTarget(rowId(fieldId, atEdge ? (step === -1 ? "down" : "up") : step === -1 ? "up" : "down"));
  }

  // --- la vista in uso e le altre ---
  const source = t(SOURCE_KEYS[resolved.source], { organization });
  const saved = resolved.source !== "device";
  const company = server?.company ?? null;
  const personal = hasPersonalViews ? (server?.personal ?? null) : null;
  // Tornare alla vista dell'azienda (o a quella iniziale, se l'azienda non ne ha una) toglie la vista personale e la
  // disposizione del dispositivo.
  // Un ordinamento diverso da quello della vista in uso è da salvare come una disposizione (LEAD-17).
  const sortToSave = sort !== undefined && !sameSort(sort.current, sort.saved) ? sort.current : null;
  const canUseShared = resolved.source === "device" || resolved.source === "personal" || sortToSave !== null;
  const canUseMine = (resolved.source === "device" || sortToSave !== null) && personal !== null;
  const [useError, setUseError] = useState<unknown>(null);

  async function backToShared() {
    setUseError(null);
    try {
      if (personal !== null) await clearMine.mutateAsync();
      clearDevice();
      sort?.onAdopted();
    } catch (error) {
      setUseError(error);
    }
  }

  /** «Usa la tua vista salvata»: le sue colonne e il suo ordinamento. */
  function backToMine() {
    clearDevice();
    sort?.onAdopted();
  }

  // --- salvataggi ---
  const [saveError, setSaveError] = useState<unknown>(null);
  async function saveForMe() {
    setSaveError(null);
    try {
      await saveMine.mutateAsync({ columns, ...(sort === undefined ? {} : { sort: sort.current }) });
      // Salvata, il pulsante sparisce: il fuoco passa a «Chiudi», accanto, e non si perde nella pagina.
      setFocusTarget(closeId);
      clearDevice(columns);
      sort?.onAdopted();
      toast.show(t("tableView.savedMine"), "success");
    } catch (error) {
      setSaveError(error);
    }
  }

  const [publishError, setPublishError] = useState<unknown>(null);
  async function publishForAll() {
    setPublishError(null);
    // Le colonne da pubblicare restano sul dispositivo finché la pubblicazione non è riuscita: se un collega pubblica
    // nel frattempo, il `409` rilegge la vista e azzera anche la vista personale di chi pubblica, ma la tabella resta la
    // sua, e confermare di nuovo pubblica proprio queste colonne, non quelle del collega.
    const attempt = columns;
    const wasDevice = resolved.source === "device";
    pin(attempt);
    try {
      await publish.mutateAsync({ columns: attempt, ...(sort === undefined ? {} : { sort: sort.current }), expectedVersion: company?.version ?? 0 });
      // La vista dell'azienda in uso non ha più nulla da salvare: il fuoco torna a «Chiudi», non al pulsante che sparisce.
      setFocusTarget(closeId);
      clearDevice(attempt);
      sort?.onAdopted();
      setConfirming(false);
      toast.show(t("tableView.savedCompany", { organization }), "success");
    } catch (error) {
      // Un errore che non è un conflitto non ha cambiato nulla sul server: la vista torna quella di prima.
      if (!isConflict(error) && !wasDevice) clearDevice(attempt);
      setPublishError(error);
    }
  }

  const conflict = isConflict(publishError);
  const canPublish = server?.canPublish ?? false;
  const commercialView = tab.commercialView ?? null;

  // Che cosa c'è da salvare (decisione P25): per sé la disposizione non ancora salvata; per tutta l'azienda anche la
  // vista personale in uso. La vista dell'azienda o quella iniziale non hanno nulla da salvare.
  const showSaveMine = hasPersonalViews && (resolved.source === "device" || sortToSave !== null);
  // L'errore di «Salva per me» vale finché c'è il suo pulsante: tornati a una vista salvata («Torna alla vista
  // iniziale», «Usa la vista di …», «Usa la tua vista salvata») sparisce con lui, e una modifica nuova riparte pulita.
  if (!showSaveMine && saveError !== null) setSaveError(null);
  const somethingForAll = resolved.source === "device" || resolved.source === "personal" || sortToSave !== null;
  // Chi può pubblicare lo dice il server: finché la vista non è arrivata, né il pulsante né il motivo.
  const showPublish = layoutReady && somethingForAll && canPublish;
  const showPublishReason = layoutReady && somethingForAll && !canPublish;

  const footer = (
    <>
      {saveError ? (
        <Reason tone="warning" role="alert">
          {messageForError(t, saveError)}
        </Reason>
      ) : null}
      {showPublishReason ? <p className="g-cols__foot-text">{t("tableView.reasonCompany")}</p> : null}
      <Button id={closeId} onClick={onClose}>
        {t("tableView.close")}
      </Button>
      {showPublish ? (
        <Button aria-haspopup="dialog" onClick={() => setConfirming(true)} disabled={!canSave}>
          {t("tableView.saveCompany", { organization })}
        </Button>
      ) : null}
      {showSaveMine ? (
        <Button variant="primary" onClick={() => void saveForMe()} disabled={!canSave || saveMine.isPending}>
          {saveMine.isPending ? t("common.saving") : t("tableView.saveMine")}
        </Button>
      ) : null}
    </>
  );

  return (
    <>
      <Drawer open title={t("tableView.title")} onClose={onClose} footer={footer}>
        <div className="g-cols">
          {/* Le parole che i pulsanti delle righe ripetono con il nome della colonna («Mostra Cellulare»). */}
          <span id={showId} hidden>
            {t("tableView.show")}
          </span>
          <span id={upId} hidden>
            {t("tableView.moveUp")}
          </span>
          <span id={downId} hidden>
            {t("tableView.moveDown")}
          </span>

          <div className="g-cols__view">
            <p className="g-cols__context">
              <span>{t("tableView.contextLabel")}</span> <strong>{context}</strong>
            </p>
            {layoutStatus === "error" ? (
              <div className="g-cols__problem">
                <Reason tone="warning" role="alert" id={layoutErrorId}>
                  {t("tableView.layoutFailed")}
                </Reason>
                <Button aria-describedby={layoutErrorId} onClick={retryLayout}>
                  {t("common.retry")}
                </Button>
              </div>
            ) : (
              <p className="g-cols__using" aria-live="polite">
                {layoutReady ? t("tableView.using", { source }) : t("common.loading")}
              </p>
            )}
            {layoutReady && sortToSave !== null ? <p className="g-cols__hint">{t("tableView.sortUnsaved", sortDescription(t, catalog, sortToSave))}</p> : null}
            {layoutReady ? <p className="g-cols__hint">{saved ? t("tableView.savedHint") : t("tableView.unsavedHint")}</p> : null}
            {catalogStatus === "error" ? (
              <div className="g-cols__problem">
                <Reason tone="warning" role="alert" id={catalogErrorId}>
                  {t("tableView.catalogFailed")}
                </Reason>
                <Button aria-describedby={catalogErrorId} onClick={retryCatalog}>
                  {t("common.retry")}
                </Button>
              </div>
            ) : null}
            {layoutReady && (canUseShared || canUseMine) ? (
              <div className="g-cols__row">
                {canUseMine ? <Button onClick={backToMine}>{t("tableView.useMine")}</Button> : null}
                {canUseShared ? (
                  <Button onClick={() => void backToShared()} disabled={clearMine.isPending}>
                    {company ? t("tableView.useCompany", { organization }) : t("tableView.useProduct")}
                  </Button>
                ) : null}
              </div>
            ) : null}
            {useError ? (
              <Reason tone="warning" role="alert">
                {messageForError(t, useError)}
              </Reason>
            ) : null}
          </div>

          <label className="g-visually-hidden" htmlFor={findId}>
            {t("tableView.find")}
          </label>
          <input
            ref={findRef}
            id={findId}
            className="g-field__input g-cols__find"
            type="search"
            value={find}
            autoComplete="off"
            placeholder={t("tableView.find")}
            onChange={(event) => setFind(event.target.value)}
          />
          {nothing ? <p className="g-cols__hint">{t("tableView.noMatches", { q: find.trim() })}</p> : null}

          {fixedMatches || shownMatches.length > 0 ? (
            <>
              <h3 id={shownId} className="g-cols__title">
                {t("tableView.shown")}
              </h3>
              <ol className="g-cols__list" aria-labelledby={shownId}>
                {fixedMatches && fixed ? (
                  <li className="g-cols__item" data-fixed="true">
                    <Icon name="lock" className="g-cols__lock" />
                    <span className="g-cols__name">{fixed.def.label}</span>
                    <span className="g-cols__note">{t("tableView.leadFixed")}</span>
                  </li>
                ) : null}
                {shownMatches.map(({ column, def }) => {
                  const position = shown.findIndex((entry) => entry.column.fieldId === column.fieldId);
                  return (
                    <ColumnRow key={column.fieldId} def={def} visible showId={showId} rowId={rowId} onToggle={toggle}>
                      <button
                        id={rowId(column.fieldId, "up")}
                        type="button"
                        className="g-cols__move"
                        aria-labelledby={`${upId} ${rowId(column.fieldId, "name")}`}
                        disabled={position === 0}
                        onClick={() => move(column.fieldId, -1)}
                      >
                        <Icon name="up" />
                      </button>
                      <button
                        id={rowId(column.fieldId, "down")}
                        type="button"
                        className="g-cols__move"
                        aria-labelledby={`${downId} ${rowId(column.fieldId, "name")}`}
                        disabled={position === shown.length - 1}
                        onClick={() => move(column.fieldId, 1)}
                      >
                        <Icon name="down" />
                      </button>
                    </ColumnRow>
                  );
                })}
              </ol>
            </>
          ) : null}

          {groups.length > 0 ? (
            <section className="g-cols__others" aria-labelledby={othersId}>
              <h3 id={othersId} className="g-cols__title">
                {t("tableView.others")}
              </h3>
              {companyFull ? (
                <Reason id={companyFullId}>{t("tableView.companyFieldsFull", { max: LEAD_LIST_FIELDS_MAX })}</Reason>
              ) : null}
              {groups.map(({ group, entries: groupEntries }) => {
                const headingId = `${baseId}-group-${group}`;
                return (
                  <div key={group} className="g-cols__group">
                    <h4 id={headingId} className="g-cols__group-title">
                      {t(`tableView.groups.${group}`)}
                    </h4>
                    <ul className="g-cols__list" aria-labelledby={headingId}>
                      {groupEntries.map(({ column, def }) => (
                        <ColumnRow
                          key={column.fieldId}
                          def={def}
                          visible={false}
                          showId={showId}
                          rowId={rowId}
                          onToggle={toggle}
                          blockedBy={blockedReason(column.fieldId)}
                        />
                      ))}
                    </ul>
                  </div>
                );
              })}
            </section>
          ) : null}

          <p className="g-cols__hint">{t("tableView.intro")}</p>
        </div>
      </Drawer>
      {/* La conferma accanto al pannello, non dentro: un Esc chiude soltanto lei. */}
      {confirming ? (
        <Dialog
          open
          title={t("tableView.saveCompany", { organization })}
          onClose={() => {
            setConfirming(false);
            setPublishError(null);
          }}
          footer={
            <>
              <Button
                onClick={() => {
                  setConfirming(false);
                  setPublishError(null);
                }}
              >
                {t("common.cancel")}
              </Button>
              <Button variant="primary" onClick={() => void publishForAll()} disabled={publish.isPending}>
                {publish.isPending ? t("common.saving") : t("tableView.confirmCompany", { organization })}
              </Button>
            </>
          }
        >
          <div className="g-cols__confirm">
            <p className="g-cols__context">
              <span>{t("tableView.contextLabel")}</span> <strong>{context}</strong>
            </p>
            <Reason tone="warning">{t("tableView.warnCompany", { organization })}</Reason>
            {commercialView ? <Reason tone="warning">{t("tableView.commercialViewWarning", { name: commercialView.displayName })}</Reason> : null}
            {publishError ? (
              <Reason tone="warning" role="alert">
                {conflict ? t("tableView.conflictCompany", { organization }) : messageForError(t, publishError)}
              </Reason>
            ) : null}
          </div>
        </Dialog>
      ) : null}
    </>
  );
}

/** Il `409` della pubblicazione: la vista dell'azienda è cambiata intanto. */
function isConflict(error: unknown): boolean {
  return error instanceof ApiRequestError && error.code === "version_conflict";
}

type RowProps = {
  def: ColumnDef;
  visible: boolean;
  showId: string;
  rowId: (fieldId: string, part: string) => string;
  onToggle: (fieldId: string, visible: boolean) => void;
  /** L'identificativo del motivo per cui la colonna non si può mostrare adesso; senza, si mostra. */
  blockedBy?: string | undefined;
  children?: ReactNode;
};

/** Una colonna nel pannello: «Mostra» con il suo nome e, per quelle nella tabella, «Sposta su» e «Sposta giù». */
function ColumnRow({ def, visible, showId, rowId, onToggle, blockedBy, children }: RowProps) {
  const checkId = rowId(def.id, "show");
  const nameId = rowId(def.id, "name");
  return (
    <li className="g-cols__item">
      <input
        id={checkId}
        type="checkbox"
        className="g-check__input"
        checked={visible}
        disabled={blockedBy !== undefined}
        aria-describedby={blockedBy}
        aria-labelledby={`${showId} ${nameId}`}
        onChange={(event) => onToggle(def.id, event.target.checked)}
      />
      <label id={nameId} htmlFor={checkId} className="g-cols__name" title={def.fullLabel}>
        {def.label}
      </label>
      {children}
    </li>
  );
}
